import React, { useState, useEffect } from 'react';
import { 
  Sparkles, 
  Target, 
  Trophy, 
  StopCircle, 
  FileText, 
  ArrowUpCircle,
  Play,
  Save,
  RotateCw,
  Cpu,
  TrendingUp,
  HelpCircle,
  ChevronDown
} from 'lucide-react';
import { ActionType, PageContext, AIResponse } from '../types';
import { executeFlowAction } from '../services/gemini';
import { auth, db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useToast } from './Toast';
import { markChecklistItemCompleted } from '../src/lib/checklist';

const INITIAL_CONTEXT: PageContext = {
  productName: "",
  description: "",
  currentHeadline: "",
  currentCTA: "",
  traffic: 0,
  conversionsA: 0,
  conversionsB: 0,
  ctrA: 0,
  ctrB: 0,
  bounceRate: 0,
  avgTimeOnPage: 0,
  daysActive: 0,
  totalLeads: 0,
  trafficSource: "Todos",
  deviceType: "Todos",
  plan: 'trial'
};

export const Optimizer: React.FC = () => {
  const { showToast } = useToast();
  const [context, setContext] = useState<PageContext>(INITIAL_CONTEXT);
  const [loading, setLoading] = useState<boolean>(false);
  const [history, setHistory] = useState<AIResponse[]>([]);
  const [activeTab, setActiveTab] = useState<'config' | 'results'>('config');
  const [pages, setPages] = useState<any[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string>('');
  const [ctaComparison, setCtaComparison] = useState<{ a: string, b: string, winner: string, reason: string, scoreA: string, scoreB: string } | null>(null);
  const [headlineVariations, setHeadlineVariations] = useState<string[]>([]);
  const [generatingHeadlines, setGeneratingHeadlines] = useState(false);
  const [turboQuantActive, setTurboQuantActive] = useState(false);
  const [turboQuantResult, setTurboQuantResult] = useState<{ speed: string, acceleration: string, impact: string, recommendation: string } | null>(null);
  const [results, setResults] = useState<{ 
    headline: { score: number, prediction: string, suggestion: string }, 
    copy: { score: number, prediction: string, suggestion: string }, 
    visual: { score: number, prediction: string, suggestion: string },
    overall: number 
  } | null>(null);

  useEffect(() => {
    const fetchPages = async () => {
      if (!auth.currentUser) return;
      try {
        const q = query(collection(db, 'landing_pages'), where('userId', '==', auth.currentUser.uid));
        const snapshot = await getDocs(q);
        setPages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Error fetching pages:", error);
      }
    };
    fetchPages();
  }, []);

  const handlePageSelect = (pageId: string) => {
    const page = pages.find(p => p.id === pageId);
    if (page) {
      setContext({
        ...INITIAL_CONTEXT,
        productName: page.name || "",
        description: page.description || "",
        currentHeadline: page.headline || "",
        currentCTA: page.cta || "",
        traffic: page.traffic || 0,
        conversionsA: page.conversions || 0,
        totalLeads: page.conversions || 0,
      });
      setSelectedPageId(pageId);
      showToast(`Dados da página "${page.name}" carregados!`, 'success');
    }
  };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const numericFields = ['traffic', 'conversionsA', 'conversionsB', 'ctrA', 'ctrB', 'bounceRate', 'avgTimeOnPage', 'daysActive', 'totalLeads'];
    setContext(prev => ({
      ...prev,
      [name]: numericFields.includes(name)
        ? Number(value) 
        : value
    }));
  };

  const generateHeadlines = async () => {
    if (!context.productName || !context.description) {
      showToast('Preencha o nome e a descrição do produto primeiro.', 'error');
      return;
    }

    setGeneratingHeadlines(true);
    try {
      const response = await executeFlowAction('gerar_headlines', context);
      
      // Sanitização robusta da resposta
      let cleaned = response.trim();
      if (cleaned.startsWith('```json')) cleaned = cleaned.replace(/^```json\n?/, '').replace(/\n?```$/, '');
      if (cleaned.startsWith('```')) cleaned = cleaned.replace(/^```\n?/, '').replace(/\n?```$/, '');
      
      let variations: string[] = [];
      try {
        const parsed = JSON.parse(cleaned);
        
        if (parsed.error) {
          showToast(parsed.error, 'error');
          return;
        }

        variations = parsed.headlines || parsed.variacoes || [];
        
        // Se não for um array claro, procura qualquer array no objeto
        if (!Array.isArray(variations)) {
          const firstArray = Object.values(parsed).find(val => Array.isArray(val));
          variations = Array.isArray(firstArray) ? firstArray : [];
        }
      } catch (e) {
        // Fallback para texto bruto se o JSON falhar (extraindo linhas numeradas)
        variations = response
          .split('\n')
          .filter(line => line.trim().match(/^\d\./))
          .map(line => line.replace(/^\d\.\s*/, '').trim());
      }
      
      if (variations.length === 0 && !response.includes('{')) {
        // Se não conseguiu parsear nem achar linhas numeradas, usa a resposta inteira como 1 variação
        variations = [response.trim()];
      }

      setHeadlineVariations(variations.length > 0 ? variations : ["O motor Neural retornou um formato inesperado. Tente gerar novamente."]);
      showToast('Variações geradas com sucesso!', 'success');
      markChecklistItemCompleted('optimize');
    } catch (error) {
      console.error("Headline Gen Error:", error);
      showToast('O motor de IA está em calibração. Tente novamente em instantes.', 'error');
    } finally {
      setGeneratingHeadlines(false);
    }
  };

  const runAction = async (action: ActionType) => {
    if (!context.productName || !context.description) {
      showToast('Por favor, preencha o nome e a descrição do produto.', 'warning');
      return;
    }
    setActiveTab('results');
    setLoading(true);
    
    try {
      const rawResult = await executeFlowAction(action, context);
      
      // Sanitização robusta da resposta JSON da IA
      let cleaned = rawResult.trim();
      if (cleaned.startsWith('```json')) cleaned = cleaned.replace(/^```json\n?/, '').replace(/\n?```$/, '');
      else if (cleaned.startsWith('```')) cleaned = cleaned.replace(/^```\n?/, '').replace(/\n?```$/, '');
      
      const parsed = JSON.parse(cleaned);

      if (parsed.code === 'MISSING_API_KEY' || (parsed.error && (parsed.error.includes('Chave de API') || parsed.error.includes('API_KEY')))) {
        showToast('Configuração de IA Pendente. Vá em Configurações para ativar o motor Quantum.', 'info');
        return;
      }

      if (parsed.error) {
        showToast(parsed.error, 'error');
        return;
      }

      const newResponse: AIResponse = {
        raw: rawResult,
        parsed,
        action,
        timestamp: Date.now()
      };

      setHistory(prev => [newResponse, ...prev]);
      
      if (action === 'prever_conversao') {
        const score = parsed['Potencial de Conversão'] || parsed['score'] || parsed['potencial'] || 80;
        setResults({
          headline: { 
            score: Math.min(100, (score as number) + 5), 
            prediction: parsed['Diagnostico_Headline'] || 'Análise neural completa.', 
            suggestion: parsed['Recomendacao_Estrategica'] || 'Mantenha o foco na promessa.' 
          },
          copy: { 
            score: Math.min(100, (score as number) - 5), 
            prediction: parsed['Diagnostico_Copy'] || 'Potencial de escala identificado.', 
            suggestion: 'Reforce os benefícios emocionais no corpo da página.' 
          },
          visual: { 
            score: Math.min(100, (score as number) + 10), 
            prediction: parsed['Diagnostico_Visual'] || 'Equilíbrio visual positivo.', 
            suggestion: 'O contraste do CTA está excelente.' 
          },
          overall: score as number
        });
      }
      if (action === 'comparar_cta') {
        setCtaComparison({
          a: parsed['pct_a'] || "0",
          b: parsed['pct_b'] || "0",
          winner: parsed['cta_vencedor'] || "A",
          reason: parsed['motivo'] || "",
          scoreA: parsed['pct_a'] || "0",
          scoreB: parsed['pct_b'] || "0"
        });
      }

      if (action === 'turbo_quant') {
        setTurboQuantActive(true);
        setTurboQuantResult({
          speed: parsed['velocidade'] || "0%",
          acceleration: parsed['aceleracao'] || "N/A",
          impact: parsed['impacto'] || "0%",
          recommendation: parsed['recomendacao'] || "N/A"
        });
      }

      showToast('Análise concluída com sucesso!', 'success');
      markChecklistItemCompleted('optimize');
    } catch (error) {
      console.error(error);
      showToast('Erro ao processar análise de IA.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 lg:p-10 h-full flex flex-col gap-8 animate-fade-in pb-32">
      {/* Central de Diagnóstico e Engenharia de Velocidade */}
      <div className="premium-card p-6 bg-emerald-500/5 border-emerald-500/10 space-y-4 animate-in fade-in slide-in-from-top-4 duration-500">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center text-emerald-400">
            <TrendingUp className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <h2 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-none">Módulo de Engenharia e Velocidade</h2>
            <h3 className="text-base font-black text-white tracking-tight">O que garante o carregamento em menos de 1 segundo para qualquer celular fraco?</h3>
          </div>
        </div>
        <p className="text-xs text-slate-400 leading-relaxed font-normal">
          O OptiVelo utiliza a tecnologia exclusiva <strong className="text-emerald-400">VeloSpeed™ Edge Servers</strong>. Criadores tradicionais (como WordPress/Elementor) geram códigos pesados e conexões lentas com o servidor. Nossa engenharia funciona de forma inteligente: compilamos o site em arquivos leves (HTML5/CSS puro), comprimimos imagens de forma neural sem perda visível de qualidade e distribuímos o site através de dezenas de servidores de borda (CDN Edge) pelo Brasil todo. Isso reduz dobras de atraso e garante abertura instantânea de menos de 1 segundo no 3G de qualquer celular.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-2">
          <div className="p-3 bg-white/[0.01] border border-white/5 rounded-xl">
            <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest block mb-1">Prever Conversão</span>
            <span className="text-xs text-slate-400 leading-relaxed">Nossos algoritmos analisam a copy e as estatísticas unificadas para classificar sua página em uma escala de 0 a 100 de conversão antes de veicular anúncios.</span>
          </div>
          <div className="p-3 bg-white/[0.01] border border-white/5 rounded-xl">
            <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest block mb-1">Otimizar Copy</span>
            <span className="text-xs text-slate-400 leading-relaxed">Sugere alterações imediatas de redação para quebrar objeções lógicas de forma amigável e limpa.</span>
          </div>
          <div className="p-3 bg-white/[0.01] border border-white/5 rounded-xl">
            <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest block mb-1">Comparar CTA</span>
            <span className="text-xs text-slate-400 leading-relaxed">Cria botões de clique alternativos e avalia de maneira estatística qual deles tem o maior poder magnético de atração para o clique.</span>
          </div>
          <div className="p-3 bg-white/[0.01] border border-white/5 rounded-xl">
            <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest block mb-1">TurboQuant</span>
            <span className="text-xs text-slate-400 leading-relaxed">Gera diagnósticos avançados sobre a integridade código-imagem, recomendando acelerações que diminuem o tempo de resposta do celular do comprador.</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Left Panel: Configuration & Actions */}
      <div className="w-full lg:w-1/3 flex flex-col gap-8">
        
        <div className="premium-card p-6 space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <Cpu className="w-4 h-4 text-indigo-400" />
              Parâmetros do Fluxo
            </h2>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setContext(INITIAL_CONTEXT)}
                className="p-2 text-slate-500 hover:text-white transition-colors"
              >
                <RotateCw className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <label className="micro-label">Importar de Landing Page</label>
                <div className="group relative">
                  <HelpCircle className="w-3 h-3 text-slate-500 cursor-help hover:text-indigo-400 transition-colors" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[10px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                    Este campo é opcional e serve para carregar dados de uma página já existente para análise e otimização.
                  </div>
                </div>
              </div>
              <div className="relative">
                <select 
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:border-indigo-500 focus:outline-none appearance-none transition-all"
                  value={selectedPageId}
                  onChange={(e) => handlePageSelect(e.target.value)}
                >
                  <option value="">Selecionar página...</option>
                  {pages.map(page => (
                    <option key={page.id} value={page.id}>{page.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="micro-label">Nome do Produto</label>
              <input 
                type="text" 
                name="productName"
                value={context.productName}
                onChange={handleInputChange}
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:border-indigo-500 focus:outline-none transition-all"
              />
            </div>
            <div className="space-y-1.5">
              <label className="micro-label">Descrição</label>
              <textarea 
                name="description"
                rows={3}
                value={context.description}
                onChange={handleInputChange}
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:border-indigo-500 focus:outline-none resize-none transition-all"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
               <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="micro-label">Título Atual</label>
                  <button 
                    onClick={generateHeadlines}
                    disabled={generatingHeadlines}
                    className="flex items-center gap-1 text-[10px] font-bold text-indigo-400 hover:text-indigo-300 transition-colors disabled:opacity-50"
                  >
                    {generatingHeadlines ? (
                      <RotateCw className="w-3 h-3 animate-spin" />
                    ) : (
                      <Sparkles className="w-3 h-3" />
                    )}
                    GERAR VARIAÇÕES
                  </button>
                </div>
                <input 
                  type="text" 
                  name="currentHeadline"
                  value={context.currentHeadline}
                  onChange={handleInputChange}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:border-indigo-500 focus:outline-none transition-all"
                />
                
                {headlineVariations.length > 0 && (
                  <div className="mt-2 space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                    {headlineVariations.map((variation, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setContext(prev => ({ ...prev, currentHeadline: variation }));
                          setHeadlineVariations([]);
                        }}
                        className="w-full text-left p-2 bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 rounded-lg text-[11px] text-slate-400 hover:text-white transition-all group"
                      >
                        <div className="flex items-start gap-2">
                          <span className="mt-0.5 text-indigo-500 font-bold opacity-50 group-hover:opacity-100">{i + 1}</span>
                          <span>{variation}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="micro-label">CTA</label>
                <input 
                  type="text" 
                  name="currentCTA"
                  value={context.currentCTA}
                  onChange={handleInputChange}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:border-indigo-500 focus:outline-none transition-all"
                />
              </div>
            </div>

            <div className="pt-6 border-t border-white/5 space-y-4">
              <div className="flex items-center justify-between">
                <label className="micro-label">Dados para Análise</label>
                <button 
                  onClick={() => {
                    setContext(prev => ({
                      ...prev,
                      traffic: Math.floor(Math.random() * 5000) + 1000,
                      conversionsA: Math.floor(Math.random() * 100) + 20,
                      totalLeads: Math.floor(Math.random() * 100) + 20,
                      ctrA: Number((Math.random() * 5).toFixed(1)),
                      bounceRate: Math.floor(Math.random() * 20) + 30,
                      avgTimeOnPage: Math.floor(Math.random() * 60) + 90
                    }));
                    showToast('Dados de teste carregados. Clique em "Prever" para analisar.', 'info');
                  }}
                  className="px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-lg text-[10px] font-black text-indigo-400 hover:bg-indigo-500/20 transition-all uppercase tracking-[0.1em]"
                >
                  Carregar Dados de Exemplo
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                   <div className="flex items-center gap-2">
                     <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">Tráfego</span>
                     <div className="group relative">
                       <HelpCircle className="w-3 h-3 text-slate-700 cursor-help hover:text-indigo-400 transition-colors" />
                       <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[10px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                         Número total de visitantes únicos na sua página.
                       </div>
                     </div>
                   </div>
                   <input type="number" name="traffic" value={context.traffic} onChange={handleInputChange} className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-sm text-white" />
                </div>
                <div className="space-y-1.5">
                   <div className="flex items-center gap-2">
                     <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">Leads Totais</span>
                     <div className="group relative">
                       <HelpCircle className="w-3 h-3 text-slate-700 cursor-help hover:text-indigo-400 transition-colors" />
                       <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[10px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                         Quantidade de contatos ou vendas geradas.
                       </div>
                     </div>
                   </div>
                   <input type="number" name="totalLeads" value={context.totalLeads} onChange={handleInputChange} className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-sm text-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                   <div className="flex items-center gap-2">
                     <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">Conversões A</span>
                     <div className="group relative">
                       <HelpCircle className="w-3 h-3 text-slate-700 cursor-help hover:text-indigo-400 transition-colors" />
                       <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[10px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                         Número de conversões da variação atual (A) para comparação.
                       </div>
                     </div>
                   </div>
                   <input type="number" name="conversionsA" value={context.conversionsA} onChange={handleInputChange} className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-sm text-white" />
                </div>
                <div className="space-y-1.5">
                   <div className="flex items-center gap-2">
                     <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">Conversões B</span>
                     <div className="group relative">
                       <HelpCircle className="w-3 h-3 text-slate-700 cursor-help hover:text-indigo-400 transition-colors" />
                       <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[10px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                         Número de conversões da nova variação (B) para teste A/B.
                       </div>
                     </div>
                   </div>
                   <input type="number" name="conversionsB" value={context.conversionsB} onChange={handleInputChange} className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-sm text-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                   <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">CTR A (%)</span>
                   <input type="number" name="ctrA" value={context.ctrA} onChange={handleInputChange} className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-sm text-white" />
                </div>
                <div className="space-y-1.5">
                   <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">CTR B (%)</span>
                   <input type="number" name="ctrB" value={context.ctrB} onChange={handleInputChange} className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-sm text-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                   <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Taxa Rejeição (%)</span>
                   <input type="number" name="bounceRate" value={context.bounceRate} onChange={handleInputChange} className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-sm text-white" />
                </div>
                <div className="space-y-1.5">
                   <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Tempo Médio (s)</span>
                   <input type="number" name="avgTimeOnPage" value={context.avgTimeOnPage} onChange={handleInputChange} className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-sm text-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                   <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Fonte de Tráfego</span>
                   <select name="trafficSource" value={context.trafficSource} onChange={handleInputChange} className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none">
                     <option value="Todos">Todos</option>
                     <option value="Google Ads">Google Ads</option>
                     <option value="Facebook Ads">Facebook Ads</option>
                     <option value="Orgânico">Orgânico</option>
                     <option value="E-mail">E-mail</option>
                   </select>
                </div>
                <div className="space-y-1.5">
                   <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Dispositivo</span>
                   <select name="deviceType" value={context.deviceType} onChange={handleInputChange} className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none">
                     <option value="Todos">Todos</option>
                     <option value="Mobile">Celular</option>
                     <option value="Desktop">Computador</option>
                     <option value="Tablet">Tablet</option>
                   </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
            <FlowButton 
              icon={Sparkles} 
              label="Gerar Variação" 
              desc="Criar novos textos"
              onClick={() => runAction('gerar_variacao')}
              disabled={loading}
              primary
            />
            <div className="grid grid-cols-2 gap-3">
              <FlowButton 
                icon={Target} 
                label="Prever" 
                desc="Pontuação de conversão"
                onClick={() => runAction('prever_conversao')}
                disabled={loading}
                tooltip="A IA analisa seu texto e métricas para dar uma nota de 0 a 100 sobre o potencial de conversão da sua página."
              />
              <FlowButton 
                icon={TrendingUp} 
                label="Otimizar" 
                desc="Refinar texto atual"
                onClick={() => runAction('otimizar_copy')}
                disabled={loading}
                tooltip="Sugere melhorias diretas no seu texto atual para torná-lo mais persuasivo e focado em vendas."
              />
              <FlowButton 
                icon={Trophy} 
                label="Comparar CTA" 
                desc="Gerar e comparar CTAs"
                onClick={() => runAction('comparar_cta')}
                disabled={loading}
                tooltip="Cria duas variações de botões (CTA) e diz qual delas tem mais chance de converter seu público."
              />
              <FlowButton 
                icon={ArrowUpCircle} 
                label="TurboQuant" 
                desc="Acelerar conversão"
                onClick={() => runAction('turbo_quant')}
                disabled={loading}
                turbo
                tooltip="Análise avançada que identifica os maiores gargalos de conversão e sugere mudanças de alto impacto."
              />
            </div>
        </div>
      </div>

      {/* Right Panel: Output Console */}
      <div className="flex-1 flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <h2 className="text-sm font-bold text-white">Saída do Fluxo</h2>
          <div className="flex items-center gap-3 px-3 py-1.5 bg-white/[0.03] border border-white/5 rounded-full">
            <div className={`w-2 h-2 rounded-full ${loading ? 'bg-indigo-500 animate-pulse' : 'bg-slate-700'}`}></div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{loading ? 'Processando' : 'Inativo'}</span>
          </div>
        </div>

        <div className="flex-1 premium-card p-8 overflow-y-auto custom-scrollbar relative">
          {history.length === 0 && !loading && (
            <div className="h-full flex flex-col items-center justify-center text-slate-600">
              <div className="w-16 h-16 bg-white/[0.02] rounded-3xl flex items-center justify-center mb-6">
                <Cpu className="w-8 h-8 opacity-20" />
              </div>
              <p className="text-sm font-medium">Sistema pronto para comandos</p>
              <p className="text-xs opacity-40 mt-2">Selecione uma ação à esquerda para começar a otimização.</p>
            </div>
          )}

          {loading && (
             <div className="flex flex-col items-center justify-center py-20 space-y-4">
                <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                <p className="micro-label animate-pulse">Executando Fluxo de IA...</p>
             </div>
          )}

          <div className="space-y-8">
            {turboQuantActive && turboQuantResult && (
              <div className="animate-in fade-in zoom-in duration-500">
                <div className="flex items-center gap-4 mb-4">
                  <span className="px-3 py-1 bg-indigo-500/10 text-indigo-400 text-[10px] font-bold rounded-full uppercase tracking-widest border border-indigo-500/20 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-ping"></div>
                    Motor TurboQuant Ativo
                  </span>
                  <div className="flex-1 h-px bg-white/5"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="premium-card p-4 border-indigo-500/20 bg-indigo-500/5">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Velocidade</div>
                    <div className="text-2xl font-black text-white">{turboQuantResult.speed}</div>
                  </div>
                  <div className="premium-card p-4 border-emerald-500/20 bg-emerald-500/5">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Impacto</div>
                    <div className="text-2xl font-black text-white">{turboQuantResult.impact}</div>
                  </div>
                  <div className="premium-card p-4 border-white/10">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Status</div>
                    <div className="text-xs font-bold text-indigo-400">OTIMIZAÇÃO QUANTUM</div>
                  </div>
                </div>

                <div className="mt-4 p-5 bg-white/[0.02] border border-white/5 rounded-2xl space-y-3">
                  <div>
                    <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest block mb-1">Aceleração Aplicada</span>
                    <p className="text-sm text-white font-medium">{turboQuantResult.acceleration}</p>
                  </div>
                  <div className="pt-3 border-t border-white/5">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Recomendação Técnica</span>
                    <p className="text-xs text-slate-400 italic">"{turboQuantResult.recommendation}"</p>
                  </div>
                </div>
              </div>
            )}

            {ctaComparison && (
              <div className="animate-in fade-in slide-in-from-top-4 duration-500">
                <div className="flex items-center gap-4 mb-4">
                  <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 text-[10px] font-bold rounded-full uppercase tracking-widest border border-emerald-500/20">
                    Comparação de CTA Ativa
                  </span>
                  <div className="flex-1 h-px bg-white/5"></div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Variation A */}
                  <div className={`premium-card p-6 space-y-4 border-2 transition-all ${ctaComparison.winner === 'A' ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-white/5'}`}>
                    <div className="flex justify-between items-start">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Variação A</span>
                      <div className="text-xl font-black text-white">{ctaComparison.scoreA}%</div>
                    </div>
                    <p className="text-lg font-bold text-white leading-tight">{ctaComparison.a}</p>
                    {ctaComparison.winner === 'A' && (
                      <div className="flex items-center gap-2 text-emerald-400 text-[10px] font-bold uppercase">
                        <Trophy className="w-3 h-3" /> Vencedor Sugerido
                      </div>
                    )}
                  </div>

                  {/* Variation B */}
                  <div className={`premium-card p-6 space-y-4 border-2 transition-all ${ctaComparison.winner === 'B' ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-white/5'}`}>
                    <div className="flex justify-between items-start">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Variação B</span>
                      <div className="text-xl font-black text-white">{ctaComparison.scoreB}%</div>
                    </div>
                    <p className="text-lg font-bold text-white leading-tight">{ctaComparison.b}</p>
                    {ctaComparison.winner === 'B' && (
                      <div className="flex items-center gap-2 text-emerald-400 text-[10px] font-bold uppercase">
                        <Trophy className="w-3 h-3" /> Vencedor Sugerido
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 p-4 bg-white/[0.02] border border-white/5 rounded-xl">
                  <p className="text-xs text-slate-400 leading-relaxed">
                    <span className="font-bold text-indigo-400 uppercase mr-2">Motivo:</span>
                    {ctaComparison.reason}
                  </p>
                </div>
              </div>
            )}

            {history.map((item, idx) => (
              <div key={item.timestamp} className="animate-slide-up">
                <div className="flex items-center gap-4 mb-4">
                  <span className="px-3 py-1 bg-indigo-500/10 text-indigo-400 text-[10px] font-bold rounded-full uppercase tracking-widest border border-indigo-500/20">
                    {item.action}
                  </span>
                  <div className="flex-1 h-px bg-white/5"></div>
                  <span className="text-slate-600 text-[10px] font-medium">{new Date(item.timestamp).toLocaleTimeString()}</span>
                </div>
                
                <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6 group hover:border-indigo-500/30 transition-all">
                  <div className="text-sm leading-relaxed text-slate-300">
                    {item.raw}
                  </div>

                  <div className="mt-6 flex gap-3">
                     <button 
                       onClick={() => {
                         if (item.parsed?.['Nova headline'] || item.parsed?.['Headline']) {
                           setContext(prev => ({ 
                             ...prev, 
                             currentHeadline: item.parsed?.['Nova headline'] || item.parsed?.['Headline'] || prev.currentHeadline,
                             currentCTA: item.parsed?.['Novo CTA'] || item.parsed?.['CTA'] || prev.currentCTA
                           }));
                           showToast('Alterações aplicadas ao contexto!', 'success');
                         } else {
                           showToast('Nenhuma alteração detectada para aplicar.', 'warning');
                         }
                       }}
                       className="px-4 py-2 bg-indigo-500 hover:bg-indigo-400 text-white text-xs font-bold rounded-xl flex items-center gap-2 transition-all shadow-lg shadow-indigo-500/10"
                     >
                       <Save className="w-3.5 h-3.5" /> Aplicar Alterações
                     </button>
                     <button 
                        onClick={() => runAction('comparar_cta')}
                        className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-bold rounded-xl flex items-center gap-2 transition-all shadow-lg shadow-emerald-500/10"
                      >
                        <Trophy className="w-3.5 h-3.5" /> Comparar
                      </button>
                     <button 
                       onClick={() => {
                         try {
                            if (navigator.clipboard && navigator.clipboard.writeText) {
                              navigator.clipboard.writeText(item.raw || '');
                            } else {
                              const textArea = document.createElement("textarea");
                              textArea.value = item.raw || '';
                              textArea.style.position = "fixed";
                              textArea.style.opacity = "0";
                              document.body.appendChild(textArea);
                              textArea.select();
                              document.execCommand('copy');
                              document.body.removeChild(textArea);
                            }
                          } catch (err) {
                            console.warn('Falha na API Clipboard do Iframe, usando prompt:', err);
                            window.prompt('Copie o conteúdo gerado manualmente:', item.raw || '');
                          }
                         showToast('Conteúdo copiado!', 'success');
                       }}
                       className="px-4 py-2 bg-white/[0.05] hover:bg-white/[0.1] text-white text-xs font-bold rounded-xl transition-all"
                     >
                       Copiar
                     </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

// Helper Components
interface FlowButtonProps {
  icon: React.ElementType;
  label: string;
  desc: string;
  onClick: () => void;
  disabled: boolean;
  primary?: boolean;
  turbo?: boolean;
  tooltip?: string;
}

const FlowButton: React.FC<FlowButtonProps> = ({ icon: Icon, label, desc, onClick, disabled, primary, turbo, tooltip }) => {
  return (
    <button 
      onClick={onClick}
      disabled={disabled}
      className={`w-full text-left p-4 rounded-2xl border transition-all duration-300 group relative disabled:opacity-50 disabled:cursor-not-allowed ${
        primary 
          ? 'bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-500/20' 
          : turbo
          ? 'bg-white/[0.03] border-indigo-500/30 text-white hover:bg-indigo-500/10 hover:border-indigo-500/50 shadow-lg shadow-indigo-500/5'
          : 'bg-white/[0.03] border-white/10 text-white hover:bg-white/[0.05] hover:border-white/20'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-4">
          <div className={`p-2.5 rounded-xl transition-colors ${
            primary ? 'bg-white/20 text-white' : 
            turbo ? 'bg-indigo-500/20 text-indigo-400 group-hover:bg-indigo-500/30' :
            'bg-white/5 text-slate-400 group-hover:text-white'
          }`}>
            <Icon className={`w-5 h-5 ${turbo ? 'animate-pulse' : ''}`} />
          </div>
          <div>
            <div className="text-xs font-bold tracking-tight">{label}</div>
            <div className={`text-[10px] font-medium opacity-50 ${primary ? 'text-white' : 'text-slate-400'}`}>{desc}</div>
          </div>
        </div>
        {tooltip && (
          <div className="group/tooltip relative">
            <HelpCircle className="w-3 h-3 text-slate-500 cursor-help hover:text-white transition-colors" />
            <div className="absolute bottom-full right-0 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[10px] text-slate-400 opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all z-50 shadow-2xl font-normal normal-case tracking-normal">
              {tooltip}
            </div>
          </div>
        )}
      </div>
    </button>
  );
};
